import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { constant, identity } from 'lodash';
import { createStructuredSelector } from 'reselect';

// Utils
import dontUpdateForKeys from '../../utils/milanoteRecompose/dontUpdateForKeys';
import { getGridSize } from '../../utils/grid/gridSizeSelector';
import { getElementId } from '../../../common/elements/utils/elementPropertyUtils';
import { getHasImageToShow, getImageDefaultMaxWidth } from './imageElementUtils';

// Actions
import { acceptElementAttachmentUndo, uploadAttachment } from '../attachments/attachmentActions';
import { navigateToElement } from '../../reducers/navigationActions';
import { switchConnectedElementParents } from '../connections/elementConnectionsActions';
import { getCurrentBoardImagesForCarouselThunk } from '../selectors/currentBoardImagesSelector';

// Components
import Image from './Image';
import captionContainerDecorator from '../../components/caption/captionContainerDecorator';
import elementLineEdgeDropTarget from '../line/elementLineEdgeDropTarget';
import ImageColorPaletteObserver from '../../components/images/imageColorExtraction/ImageColorPaletteObserver';
import groupResizeStateDecorator from '../resizing/groupResizeStateDecorator';
import elementSaveMediaResizeDecorator from '../resizing/mediaResize/elementSaveMediaResizeDecorator';
import imageDoubleClickResizeDecorator from './imageDoubleClickResizeDecorator';
import groupResizeBehaviourDecorator from '../resizing/groupResizeBehaviourDecorator';

// Constants
import { BoardSections, RootBoardTypes } from '../../../common/boards/boardConstants';
import { IMAGE_MIN_HEIGHT_GRID, IMAGE_MIN_WIDTH_GRID } from '../../../common/images/imageConstants';
import { ELEMENT_DEFAULT_WIDTH } from '../../../common/elements/elementConstants';
import { MaintainAspectRatioMode } from '../resizing/resizeTypes';

const mapStateToProps = createStructuredSelector({
    gridSize: getGridSize,
});

const mapDispatchToProps = (dispatch) => ({
    dispatchUploadImage: (props) => dispatch(uploadAttachment(props)),
    dispatchAcceptElementAttachmentUndo: (props) => dispatch(acceptElementAttachmentUndo(props)),
    dispatchNavigateToImage: (elementId) => dispatch(navigateToElement({ elementId })),
    dispatchSwitchConnectedElementParents: (args) => dispatch(switchConnectedElementParents(args)),
    dispatchGetCurrentBoardImagesForCarousel: () => dispatch(getCurrentBoardImagesForCarouselThunk()),
});

@connect(mapStateToProps, mapDispatchToProps)
@elementLineEdgeDropTarget
@dontUpdateForKeys(['dragModifierKeys', 'canDrop'])
@captionContainerDecorator
@groupResizeStateDecorator(constant(MaintainAspectRatioMode.OPTIONAL), IMAGE_MIN_WIDTH_GRID, IMAGE_MIN_HEIGHT_GRID)
@elementSaveMediaResizeDecorator({
    getDefaultWidth: constant(ELEMENT_DEFAULT_WIDTH),
    getDefaultMaxWidth: getImageDefaultMaxWidth,
})
@imageDoubleClickResizeDecorator
@groupResizeBehaviourDecorator()
class ImageContainer extends React.Component {
    handleKey = (event) => {
        const { isSelected, captionKeyHandler, isEditing } = this.props;

        if (!isSelected) return;
        if (isEditing) return; // let tiptap handle keypresses if active

        captionKeyHandler(event, this.props);
    };

    uploadImage = (file, transactionId) => {
        const { dispatchUploadImage, element } = this.props;
        return dispatchUploadImage({
            id: getElementId(element),
            file,
            transactionId,
        });
    };

    render() {
        const {
            connectLineEdgeDropTarget,
            canConnectLineEdge,
            boardSection,
            dispatchGetCurrentBoardImagesForCarousel,
        } = this.props;

        const lineEdgeConnector =
            connectLineEdgeDropTarget && canConnectLineEdge ? connectLineEdgeDropTarget : identity;

        // Don't allow long hover image replacement if the image is in the quick notes or inbox, unless it's a
        // placeholder image
        const allowHoverReplaceImage =
            !getHasImageToShow(this.props) ||
            (boardSection !== BoardSections.INBOX && boardSection !== RootBoardTypes.QUICK_NOTES);

        return lineEdgeConnector(
            <div>
                <ImageColorPaletteObserver element={this.props.element} />
                <Image
                    {...this.props}
                    dispatchGetCurrentBoardImagesForCarousel={dispatchGetCurrentBoardImagesForCarousel}
                    allowHoverReplaceImage={allowHoverReplaceImage}
                    uploadFile={this.uploadImage}
                    handleKey={this.handleKey}
                />
            </div>,
        );
    }
}

ImageContainer.propTypes = {
    element: PropTypes.object.isRequired,
    gridSize: PropTypes.number,
    attachment: PropTypes.object,
    connectDropTarget: PropTypes.func,
    connectLineEdgeDropTarget: PropTypes.func,
    isEditing: PropTypes.bool,
    isSelected: PropTypes.bool,
    captionKeyHandler: PropTypes.func,
    dispatchCleanElementAttachment: PropTypes.func,
    dispatchAcceptElementAttachmentUndo: PropTypes.func,
    dispatchNavigateToImage: PropTypes.func,
    dispatchSwitchConnectedElementParents: PropTypes.func,
    dispatchUpdateElement: PropTypes.func.isRequired,
    dispatchUploadImage: PropTypes.func,
    dispatchGetCurrentBoardImagesForCarousel: PropTypes.func,
    inList: PropTypes.string,
    boardSection: PropTypes.string,
    canConnectLineEdge: PropTypes.bool,
};

export default ImageContainer;
